import React, { useEffect, useState } from 'react';
import CommonCheckBox from '@components/molecules/checkBox/CheckBox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MenuManageTabProps, PRODUCT_KEYS } from '@type/productType';
import { CallGetLegalNotice, CallPostLegalNotice } from '@library/request/callProduct';
import { useMenuPermission } from '@src/recoil/permission';
import { NoAuthBox } from '@src/pages/product/componnents/common';

const Origin = (props: MenuManageTabProps) => {
  const { storeId, setLoading } = props;
  const queryClient = useQueryClient();
  const { isNoAuthOrigin, menuPermission } = useMenuPermission(storeId);

  const [isEdit, setIsEdit] = useState(false);
  const [noticeData, setNoticeData] = useState({ content: '', originDisplay: false });

  // 원산지 정보 조회 API
  const { data: legalNotice } = useQuery([PRODUCT_KEYS.LEGALNOTICE, storeId], {
    enabled: !!storeId && menuPermission.isFetch && (!menuPermission.data.length || !isNoAuthOrigin),
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetLegalNotice(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
        setIsEdit(false);
      }
    },
  });

  // 원산지 정보 저장 API
  const { mutate: saveMutation } = useMutation((param: any) => CallPostLegalNotice(storeId, param), {
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (response) => {
      if (response?.result.code !== 1) {
        alert(response?.result?.message);
      } else {
        alert('원산지 정보 수정이 완료되었습니다.');
        queryClient.invalidateQueries([PRODUCT_KEYS.LEGALNOTICE, storeId]);
      }
    },
    onError: (error) => {
      console.log('error:', error);
    },
    onSettled: () => {
      setLoading(false);
      setIsEdit(false);
    },
  });

  const onClickSaveBtn = () => {
    if (!noticeData.originDisplay) {
      if (!noticeData.content?.trim()) {
        alert('원산지 정보를 입력해 주세요.');
        return;
      }
    }
    const param = {
      origin: noticeData.content,
      originDisplay: noticeData.originDisplay ? 0 : 1,
    };
    saveMutation(param);
  };

  useEffect(() => {
    if (!legalNotice) return;
    setNoticeData({ content: legalNotice?.origin?.content, originDisplay: legalNotice.originDisplay === '0' });
  }, [legalNotice]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {isNoAuthOrigin ? (
        <NoAuthBox type="origin" />
      ) : (
        <div className="origin-wrapper">
          {!noticeData.content && !isEdit ? (
            <div className="no-content">
              <div className="border-grey" />
              <img src="//image.thecupping.co.kr/partners/web/img-empty-data@2x.png" className="img-empty" alt="" />
              <p className="desc-empty">등록된 내용이 없습니다.</p>
            </div>
          ) : (
            <div id="originBox" className="sec-origin">
              <textarea
                value={noticeData.content || ''}
                id="originContent"
                className="tf-cont"
                readOnly={!isEdit || noticeData.originDisplay}
                data-string="text"
                onChange={(e) => {
                  setNoticeData((prev) => ({ ...prev, content: e.target.value }));
                }}
              />
              {isEdit && (
                <CommonCheckBox
                  label="원산지 표시 대상 품목이 없습니다."
                  id="originDisplay"
                  miniSize
                  value={noticeData.originDisplay}
                  onChange={(e) => {
                    setNoticeData((prev) => ({ ...prev, originDisplay: e.target.checked }));
                    if (!e.target.checked) {
                      setNoticeData((prev) => ({ ...prev, content: '' }));
                    } else {
                      setNoticeData((prev) => ({ ...prev, content: '원산지 표시 대상 품목이 없습니다.' }));
                    }
                  }}
                />
              )}
            </div>
          )}
          <div className="sec-notice">
            <h4 className="tit-notice">원산지 필수 표시 항목</h4>
            <a
              href="https://naqs.go.kr/contents/contentsTab.do?menuId=MN40284"
              className="link-notice"
              data-target="_blank"
            >
              https://naqs.go.kr/contents/contentsTab.do?menuId=MN40284
            </a>
            <ul className="list-notice">
              <li>
                농·축산물: 소고기, 돼지고기, 닭고기, 오리고기, 양고기, 염소고기, 배추김치(배추, 고춧가루), 쌀(밥, 죽,
                누룽지), 콩(두부류, 콩국수, 콩비지) ( ※ 축산물의 경우 식육, 포장육, 식육가공품을 포함)
              </li>
              <li>
                수산물: 광어(넙치), 우럭(조피볼락), 참돔, 미꾸라지, 뱀장어(민물장어), 낙지, 명태(건제품 제외), 고등어,
                갈치, 오징어, 꽃게, 참조기, 다랑어, 아귀, 쭈꾸미 ( ※ 수산물의 경우 해당 수산물가공품과 수족관 등에
                보관·진열하는 살아 있는 수산물을 포함)
              </li>
              <li>단, 조리 용도에 따라 원산지가 다른 경우 용도별로 각각 원산지 표시해야 합니다.</li>
              <li>
                조리음식을 배달하거나 배달앱 등을 통해 통신판매하는 경우 원산지 필수 표시항목을 포장재에 표시하되,
                포장재 표시가 어려운 경우 전단지, 스티커 또는 영수증에 표시 가능합니다.
              </li>
              <li>
                '농수산물의 원산지 표시에 관한 법률 시행규칙'이 시행됨에 따라 원산지를 꼭 표시해 주세요.
                <br />
                원산지를 표기하지 않거나 표기방법을 위반하게 되면 1천만원 이하의 과태료가 부과됩니다.
              </li>
            </ul>
          </div>
          <div className="fixedBtnGroup">
            <button
              type="button"
              id="btnSubmit"
              className="btnDone"
              onClick={() => {
                if (isEdit) {
                  onClickSaveBtn();
                } else {
                  setIsEdit(true);
                }
              }}
            >
              {isEdit ? '저장' : '수정'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Origin;
